// Stránkový CSS mimo main.css (#313, F2 rychlosti).
//
// main.css nese jen to, co může být na kterékoli URL (hlavička, patička, karty,
// výpis, detail, hledání, 404…). Stránky s VLASTNÍM tvarem URL nebo metou mají
// vlastní `dist/page-<skupina>.css` (build.mjs CSS_ENTRIES, sass entry
// src/scss/page-*.scss) a stahují ho jen ony — běžná stránka tím ušetřila
// 26 kB br z 72 (měřeno 2026-09-13).
//
// Kdo link vkládá:
//   1. RYCHLÁ CESTA — inline skript v HTML kódech hlavičky
//      (shoptet-includes/common-header.html, markery `bs-page-css:start|end`):
//      běží v <head> před obsahem, rozhoduje z `location.pathname` a z
//      `meta[property=og:type]` (detail článku má slug v kořeni URL, ale Shoptet
//      vypisuje `og:type=article` PŘED naším includem), link dostane
//      `blocking="render"` → v prohlížečích, které atribut znají, je CSS
//      render-blocking jako main.css. `body` v hlavičce neexistuje (stejná past
//      jako `bs-scloak`), proto pathname/meta, ne body třídy.
//   2. POJISTKA — `ensurePageCss()` z boot() (main.js): rozhoduje z body tříd,
//      a když link chybí (přejmenovaný admin slug, HTML kódy bez snippetu,
//      offline fixture na file://), doplní ho z href main.css. Boot drží cloak
//      `html.bs-boot`, dokud stránkový CSS nedorazí (nebo 4 s), aby se
//      rozsypaná stránka neukázala.
//
// Snippet hlavičky je minifikovaná kopie `pageCssForRequest()`, kterou pro
// každý shop generuje tools/lib/html-includes.mjs z téhož manifestu — shodu
// obou hlídá tools/unit/page-css.test.mjs nad tabulkou URL. Nová skupina =
// řádek v PAGE_CSS + CSS_ENTRIES v build.mjs + generátor snippetu.
'use strict';

import { bodyClass, page, pageSlug } from './pages.js';

// Info stránky se slugem z administrace (I #37, II #38, stav objednávky #18,
// FAQ) = klíče `pages` z manifestu shopu; stránky, které shop nemá (null),
// vypadnou. Po přejmenování slugu v adminu bez úpravy manifestu rychlá cesta
// mine a nastupuje pojistka podle `body.in-<slug>`.
export const INFO_PAGES = ['contact', 'shipping', 'store', 'b2b', 'service', 'ratings', 'claim', 'withdrawal', 'orderStatus', 'faq'];
export const INFO_SLUGS = INFO_PAGES.map(pageSlug).filter(Boolean);

const BLOG = page('blog');

// Pořadí = priorita při rozhodování (první shoda vyhrává).
export const PAGE_CSS = [
  {
    name: 'checkout',
    request: (p) => p.indexOf(page('cart')) === 0 || p.indexOf(page('checkout')) === 0,
    body: ['ordering-process'],
  },
  {
    name: 'account',
    request: (p) => p.indexOf(page('account')) === 0 || p === page('login') || p === page('register'),
    body: ['customer-page', bodyClass('login'), bodyClass('register')],
  },
  {
    // shop bez blogu (SK) nemá ani rubriky, ani detail článku
    name: 'blog',
    request: (p, ogType) => !!BLOG && (p.indexOf(BLOG) === 0 || ogType === 'article'),
    body: ['type-posts-listing', 'type-post'],
  },
  {
    name: 'info',
    request: (p) => INFO_SLUGS.indexOf(p.replace(/^\/|\/$/g, '')) >= 0 && /^\/[^/]+\/$/.test(p),
    body: INFO_SLUGS.map((s) => 'in-' + s),
  },
];

export const PAGE_CSS_NAMES = PAGE_CSS.map((r) => r.name);

// Rozhodnutí rychlé cesty (hlavička): pathname + og:type. Čistá funkce, aby ji
// devcheck mohl porovnat se snippetem nad tabulkou URL.
export function pageCssForRequest(pathname, ogType) {
  const p = String(pathname || '');
  const og = String(ogType || '');
  const hit = PAGE_CSS.find((r) => r.request(p, og));
  return hit ? hit.name : null;
}

// Rozhodnutí pojistky (boot): body třídy.
export function pageCssForBody(classList) {
  const has = (c) => !!classList && (typeof classList.contains === 'function' ? classList.contains(c) : classList.indexOf(c) >= 0);
  const hit = PAGE_CSS.find((r) => r.body.some(has));
  return hit ? hit.name : null;
}

// href stránkového CSS odvozený z linku main.css — na CDN i na file:// fixture
// (`../dist/main.css` → `../dist/page-x.css`), `?v=` zůstává.
export function pageCssHref(name, doc = document) {
  const main = doc.querySelector('link[rel="stylesheet"][href*="main.css"]');
  if (!main) return null;
  return main.getAttribute('href').replace(/main\.css/, 'page-' + name + '.css');
}

const WAIT_MS = 4000;

function waitForLink(link) {
  // Načtený link má `sheet`; hlavičkový snippet po load/error nastaví
  // `data-bs-page-css-done`, aby se na 404 nečekalo celé okno.
  if (link.sheet || link.getAttribute('data-bs-page-css-done')) return null;
  return new Promise((resolve) => {
    let done = false;
    const fin = () => {
      if (done) return;
      done = true;
      link.setAttribute('data-bs-page-css-done', '1');
      resolve();
    };
    link.addEventListener('load', fin, { once: true });
    link.addEventListener('error', fin, { once: true });
    setTimeout(fin, WAIT_MS);
  });
}

// Zajistí stránkový CSS pro aktuální stránku. Vrací `null`, když není na co
// čekat (stránka bez skupiny / link už načtený), jinak Promise, po které smí
// boot sundat cloak.
export function ensurePageCss(doc = document) {
  const name = pageCssForBody(doc.body ? doc.body.classList : null);
  let link = name ? doc.querySelector(`link[data-bs-page-css="${name}"]`) : doc.querySelector('link[data-bs-page-css]');
  if (!link) {
    if (!name) return null;
    const href = pageCssHref(name, doc);
    if (!href) return null;
    link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-bs-page-css', name);
    link.setAttribute('data-bs-page-css-src', 'boot'); // pojistka, ne hlavička — devcheck to rozlišuje
    doc.head.appendChild(link);
  }
  return waitForLink(link);
}
